// Chat Settings Manager Component
import React, { useState, useEffect } from 'react';
import { Loader2, Save, MessageCircle } from 'lucide-react';
import { Button } from '../ui/button';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ChatManager = () => {
  const [settings, setSettings] = useState({
    enabled: false,
    tawk_property_id: '',
    tawk_widget_id_en: '',
    tawk_widget_id_fr: '',
    tawk_widget_id_ar: ''
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => { fetchSettings(); }, []);

  const fetchSettings = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/chat-settings`, { credentials: 'include' });
      if (response.ok) { const data = await response.json(); setSettings(data); }
    } catch (err) { console.error('Error fetching chat settings:', err); }
    finally { setLoading(false); }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/chat-settings`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' }, credentials: 'include', body: JSON.stringify(settings)
      });
      if (response.ok) { alert('Paramètres du chat sauvegardés !'); }
    } catch (err) { console.error('Error saving chat settings:', err); }
    finally { setSaving(false); }
  };

  if (loading) {
    return <div className="flex justify-center py-12"><Loader2 className="w-8 h-8 animate-spin text-slate-400" /></div>;
  }

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-light text-slate-800">Chat en Direct (Tawk.to)</h2>
        <Button onClick={handleSave} disabled={saving}>
          {saving ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Sauvegarde...</> : <><Save className="w-4 h-4 mr-2" /> Sauvegarder</>}
        </Button>
      </div>

      <div className="space-y-6">
        {/* Enable/Disable Toggle */}
        <div className="border border-slate-200 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <MessageCircle className="w-5 h-5 text-slate-500" />
              <div>
                <h3 className="font-medium text-slate-800">Activer le chat</h3>
                <p className="text-sm text-slate-500">Afficher le widget Tawk.to sur le site</p>
              </div>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input type="checkbox" checked={settings.enabled} onChange={(e) => setSettings({...settings, enabled: e.target.checked})} className="sr-only peer" />
              <div className="w-11 h-6 bg-slate-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-green-600"></div>
            </label>
          </div>
        </div>

        {/* Tawk.to Property ID */}
        <div className="border border-slate-200 rounded-lg p-4">
          <h3 className="font-medium text-slate-800 mb-4">ID de Propriété Tawk.to</h3>
          <p className="text-sm text-slate-500 mb-3">L'ID unique de votre compte Tawk.to (trouvé dans les paramètres)</p>
          <input type="text" value={settings.tawk_property_id || ''} onChange={(e) => setSettings({...settings, tawk_property_id: e.target.value})} placeholder="ex: 6756f7c349e2fd8dfefs4b65" className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm font-mono" />
        </div>

        {/* Widget IDs per language */}
        <div className="border border-slate-200 rounded-lg p-4">
          <h3 className="font-medium text-slate-800 mb-4">IDs des Widgets par Langue</h3>
          <p className="text-sm text-slate-500 mb-4">Créez des widgets séparés dans Tawk.to pour chaque langue et entrez leurs IDs ici.</p>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">🇫🇷 Widget Français</label>
              <input type="text" value={settings.tawk_widget_id_fr || ''} onChange={(e) => setSettings({...settings, tawk_widget_id_fr: e.target.value})} placeholder="ex: 1iejkad4r" className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm font-mono" />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">🇬🇧 Widget Anglais</label>
              <input type="text" value={settings.tawk_widget_id_en || ''} onChange={(e) => setSettings({...settings, tawk_widget_id_en: e.target.value})} placeholder="ex: 1iejkad5s" className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm font-mono" />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">🇩🇿 Widget Arabe</label>
              <input type="text" value={settings.tawk_widget_id_ar || ''} onChange={(e) => setSettings({...settings, tawk_widget_id_ar: e.target.value})} placeholder="ex: 1iejkad6t" className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm font-mono" />
            </div>
          </div>
        </div>

        {/* Help box */}
        <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <h3 className="font-medium text-blue-900 mb-2">💡 Comment configurer</h3>
          <ol className="text-sm text-blue-800 space-y-1 list-decimal list-inside">
            <li>Connectez-vous à votre compte Tawk.to</li>
            <li>Créez un widget pour chaque langue (FR, EN, AR)</li>
            <li>Pour chaque widget, copiez l'ID depuis l'URL du script d'installation</li>
            <li>Le format est: tawk.to/chat/[PROPERTY_ID]/[WIDGET_ID]</li>
          </ol>
        </div>
      </div>
    </div>
  );
};

export default ChatManager;
